import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bed,
  BedDouble,
  CookingPot,
  Music,
  Sun,
  Trees,
  Umbrella,
  Waves,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToSection } from "./scroll";
import { useLang } from "@/i18n";

/* Icône par caractéristique (mots-clés FR + EN) */
const FEATURE_ICON_RULES = [
  [/piscine|pool|jacuzzi/i, Waves],
  [/carbet|galerie|gallery/i, Umbrella],
  [/cuisine|kitchen/i, CookingPot],
  [/pieds dans|feet in|salle d'eau|shower|sea-view|vue mer/i, Waves],
  [/salon|piano|living/i, Music],
  [/chambre/i, Bed],
  [/suite|master/i, BedDouble],
  [/jardin|garden/i, Trees],
];

const wx = (id, w, h) =>
  id.startsWith("/")
    ? id
    : `https://static.wixstatic.com/media/${id}/v1/fill/w_${w},h_${h},q_85/${id.split("~mv2")[0]}~mv2.jpg`;

const V = (n) => `/images/logements/villa/${n}.jpg`;
const B = (n) => `/images/logements/bungalow/${n}.jpg`;
const M = (n) => `/images/logements/marina/${n}.jpg`;

const PHOTOS = {
  lavilla: [
    V("45-galerie-table-repas"),
    V("33-terrasse-echecs"),
    V("25-chambre-terre-de-haut"),
    V("01-piscine-debordement"),
    V("03-galerie-couverte"),
    V("46-salon-sejour"),
    V("47-suite-soufriere-4"),
    V("48-chambre-terre-de-haut-3"),
    V("49-carbet-balancoire-2"),
    V("50-baignoire-ilots-2"),
    V("51-bar-tiki-3"),
    V("02-piscine-palmier"),
    V("04-vue-drone-1"),
    V("05-vue-drone-2"),
    V("06-vue-drone-3"),
    V("07-vue-drone-4"),
    V("08-piscine-aerienne"),
    V("09-carbet-balancoire"),
    V("10-carbet-salon"),
    V("11-carbet-salon-2"),
    V("12-salon-piano"),
    V("13-salon-coiffeuse"),
    V("14-terrasse-repas-1"),
    V("15-terrasse-repas-2"),
    V("16-cuisine-1"),
    V("17-cuisine-2"),
    V("18-cuisine-piano"),
    V("19-cuisine-3"),
    V("20-suite-soufriere"),
    V("21-suite-soufriere-2"),
    V("22-suite-soufriere-3"),
    V("23-bureau-soufriere"),
    V("24-soufriere-canape"),
    V("26-chambre-terre-de-haut-2"),
    V("27-chambre-houelmont"),
    V("28-chambre-houelmont-2"),
    V("29-sdb-baignoire-ilot"),
    V("30-sdb-2"),
    V("31-petite-sdb-1"),
    V("32-petite-sdb-2"),
    V("33-terrasse-echecs"),
    V("34-paillote-vue"),
    V("35-vue-terrasse"),
    V("36-terrasse"),
    V("37-terrasse-hamac"),
    V("38-vue-chambre"),
    V("39-terrasse-hamac-2"),
    V("40-bar-tiki"),
    V("41-bar-tiki-2"),
    V("42-carbet-repas"),
  ],
  bungalow: [
    B("01-pieds-dans-leau"),
    B("08-chambre-escalier"),
    B("09-chambre-terrasse"),
    B("02-chambre-vue-mer"),
    B("03-vitrage-miroir"),
    B("04-exterieur-piscine"),
    B("05-jacuzzi-ronde"),
    B("06-jacuzzi-paillote"),
    B("07-jacuzzi-paillote-2"),
    B("10-sdb-vue-mer"),
    B("11-kitchenette"),
    B("12-escalier-helice"),
  ],
  marina: [
    M("25-chambre-terre-de-bas"),
    M("26-chambre-hamac-vue"),
    M("27-chambre-bologne"),
    M("02-terrasse-repas"),
    M("03-terrasse-hamac"),
    M("05-terrasse-salon-ext"),
    M("06-salon-1"),
    M("07-salon-2"),
    M("08-salon-3"),
    M("09-salon-tv"),
    M("10-salon-biblio"),
    M("11-terrasse-repas-interieur"),
    M("13-cuisine-coin"),
    M("14-cuisine-1"),
    M("15-cuisine-2"),
    M("16-chambre-bologne"),
    M("17-chambre-bologne-2"),
    M("18-chambre-bologne-3"),
    M("19-chambre-terre-de-bas"),
    M("20-chambre-terre-de-bas-2"),
    M("21-sdb-baignoire"),
    M("22-sdb-bologne"),
    M("23-sdb-bologne-2"),
    M("24-entree"),
  ],
};

function Block({ data, id, reverse }) {
  const { isFr } = useLang();
  const [open, setOpen] = useState(null);
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);
  const photos = PHOTOS[id] || [];
  const [main, sideA, sideB] = photos;
  const extra = Math.max(photos.length - 3, 0);

  /* Diaporama automatique (La Villa) : avance toutes les 4,5 s, pause au survol,
     le minuteur repart après chaque interaction manuelle */
  const auto = id === "lavilla" && extra > 1;
  useEffect(() => {
    if (!auto || paused || open !== null) return;
    const t = setInterval(() => setCur((c) => (c + 1) % extra), 4500);
    return () => clearInterval(t);
  }, [auto, paused, open, extra, cur]);

  const go = (dir) =>
    setOpen((cur2) => (cur2 + dir + photos.length) % photos.length);

  return (
    <div id={id} data-testid={`lodging-${id}`} className="relative py-16 sm:py-20">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
          <Reveal>
            <div className="grid grid-cols-2 gap-4 sm:gap-5">
              <div className="col-span-2 clipped-frame overflow-hidden">
                <motion.img
                  src={wx(main, 1100, 760)}
                  alt={data.title}
                  loading="lazy"
                  data-testid={`lodging-${id}-photo-main`}
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="overflow-hidden">
                <motion.img
                  src={wx(sideA, 500, 660)}
                  alt={data.title}
                  loading="lazy"
                  data-testid={`lodging-${id}-photo-a`}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <div className="overflow-hidden">
                <motion.img
                  src={wx(sideB, 500, 660)}
                  alt={data.title}
                  loading="lazy"
                  data-testid={`lodging-${id}-photo-b`}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>

        <div className={`lg:col-span-7 ${reverse ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
          <Reveal>
            <p className="eyebrow mb-5">{data.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-bone leading-[1.1]">
              {data.title}
            </h2>
            <p
              className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-goldlight mt-4 leading-relaxed"
              data-testid={`lodging-${id}-spec`}
            >
              {data.spec}
            </p>
            <p className="mt-7 text-sm sm:text-base text-mist font-light leading-relaxed max-w-2xl">
              {data.p1}
            </p>
            <p className="mt-4 text-sm sm:text-base text-mist font-light leading-relaxed max-w-2xl">
              {data.p2}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-10">
              {data.features.map((f, i) => (
                <div
                  key={i}
                  data-testid={`lodging-${id}-feature-${i}`}
                  className="border-t gold-hairline py-5 px-2 -mx-2 hover:bg-moss/40 transition-colors duration-500"
                >
                  <div className="flex items-baseline gap-4">
                    <div className="shrink-0">
                      {(() => {
                        const match = FEATURE_ICON_RULES.find(([re]) => re.test(f.t));
                        const Icon = match ? match[1] : Sun;
                        return <Icon size={18} className="text-gold" aria-hidden="true" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-display text-xl text-bone">{f.t}</p>
                      <p className="text-xs sm:text-sm text-sage font-light mt-1 leading-relaxed">
                        {f.d}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid={`lodging-${id}-book`}
                onClick={() => scrollToSection("reservations")}
                className="border border-gold/50 text-gold hover:bg-gold hover:text-night px-8 py-3.5 font-mono text-[0.65rem] uppercase tracking-[0.22em] transition-all duration-300"
              >
                {data.cta} →
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      {extra > 0 && (
        <Reveal delay={0.1}>
          <div className="mt-12" data-testid={`lodging-${id}-viewer`}>
          <div
            className="relative overflow-hidden group"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
              <AnimatePresence mode="wait">
                <motion.img
                  key={cur}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  src={wx(photos[cur + 3], 1600, 900)}
                  alt={`${data.title} — ${isFr ? "photo" : "photo"} ${cur + 4}`}
                  data-testid={`lodging-${id}-viewer-photo`}
                  onClick={() => setOpen(cur + 3)}
                  className="w-full aspect-[21/9] object-cover cursor-pointer"
                />
              </AnimatePresence>
              <button
                onClick={() => setCur((c) => (c - 1 + extra) % extra)}
                data-testid={`lodging-${id}-viewer-prev`}
                aria-label={isFr ? "Photo précédente" : "Previous photo"}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center glass text-bone hover:text-gold font-mono text-xl transition-colors"
              >
                ‹
              </button>
              <button
                onClick={() => setCur((c) => (c + 1) % extra)}
                data-testid={`lodging-${id}-viewer-next`}
                aria-label={isFr ? "Photo suivante" : "Next photo"}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center glass text-bone hover:text-gold font-mono text-xl transition-colors"
              >
                ›
              </button>
              <span
                data-testid={`lodging-${id}-viewer-count`}
                className="absolute bottom-4 right-4 glass px-3 py-1.5 font-mono text-[0.6rem] tracking-[0.2em] text-bone"
              >
                {cur + 1} / {extra}
              </span>
              <span
                data-testid={`lodging-${id}-viewer-title`}
                className="absolute bottom-4 left-4 font-mono text-[0.55rem] uppercase tracking-[0.3em] text-bone/90"
              >
                {data.title}
              </span>
            </div>
            <p className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-sage mt-3">
              {isFr ? "Cliquer sur la photo pour l'agrandir" : "Click the photo to enlarge"}
            </p>
          </div>
        </Reveal>
      )}

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid={`lodging-${id}-lightbox`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-night/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setOpen(null)}
          >
            <motion.img
              key={open}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              src={wx(photos[open], 1600, 1067)}
              alt={data.title}
              className="max-h-[82vh] max-w-[92vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-gold">
              {data.title}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              data-testid={`lodging-${id}-lightbox-prev`}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-bone/70 hover:text-gold font-mono text-2xl px-3"
              aria-label={isFr ? "Précédent" : "Previous"}
            >
              ‹
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              data-testid={`lodging-${id}-lightbox-next`}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-bone/70 hover:text-gold font-mono text-2xl px-3"
              aria-label={isFr ? "Suivant" : "Next"}
            >
              ›
            </button>
            <button
              onClick={() => setOpen(null)}
              data-testid={`lodging-${id}-lightbox-close`}
              className="absolute top-5 right-6 text-bone/70 hover:text-gold font-mono text-xs uppercase tracking-[0.25em]"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Lodging() {
  const { t } = useLang();
  return (
    <section data-testid="lodgings-section" className="py-10 sm:py-14 px-5 sm:px-10">
      <div className="mx-auto max-w-[1500px]">
        <Block id="lavilla" data={t.lavilla} />
        <div className="h-px bg-[rgba(194,166,117,0.2)] my-10" />
        <Block id="bungalow" data={t.bungalow} />
        <div className="h-px bg-[rgba(194,166,117,0.2)] my-10" />
        <Block id="marina" data={t.marina} reverse />
      </div>
    </section>
  );
}
